import type { FC, ReactNode } from 'react'

import { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

type DialogProps = {
    open: boolean
    onClose: () => void
    title: ReactNode
    description?: ReactNode
    icon?: ReactNode
    tone?: 'default' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    footer?: ReactNode
    children?: ReactNode
}

const SIZES = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl'
}

// Native <dialog>: focus trapping, Escape and the top layer come from the browser.
export const Dialog: FC<DialogProps> = ({
    open,
    onClose,
    title,
    description,
    icon,
    tone = 'default',
    size = 'md',
    footer,
    children
}): ReactNode => {
    const ref = useRef<HTMLDialogElement>(null)
    const titleId = useId()
    const descriptionId = useId()

    useEffect(() => {
        const dialog = ref.current

        if (!dialog) return
        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    return (
        <dialog
            ref={ref}
            aria-labelledby={titleId}
            aria-describedby={description ? descriptionId : undefined}
            onClose={onClose}
            onCancel={event => {
                event.preventDefault()
                onClose()
            }}
            onClick={event => {
                if (event.target === event.currentTarget) onClose()
            }}
            className={cn(
                'm-auto w-[calc(100%-2rem)] overflow-hidden rounded-2xl border bg-popover p-0 text-popover-foreground shadow-float',
                'backdrop:bg-slate-950/50 backdrop:backdrop-blur-[2px] open:animate-in open:fade-in-0 open:zoom-in-95 open:duration-200',
                SIZES[size]
            )}
        >
            <div className='flex max-h-[calc(100dvh-2rem)] flex-col'>
                <div className='flex items-start gap-3.5 px-6 pt-5 pb-4'>
                    {icon && (
                        <span
                            className={cn(
                                'grid size-10 shrink-0 place-items-center rounded-full [&_svg]:size-5',
                                tone === 'danger' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                            )}
                            aria-hidden='true'
                        >
                            {icon}
                        </span>
                    )}

                    <div className='min-w-0 flex-1 pt-0.5'>
                        <h2 id={titleId} className='text-base leading-snug font-semibold tracking-tight'>
                            {title}
                        </h2>

                        {description && (
                            <div id={descriptionId} className='mt-1 text-[13px] leading-relaxed text-muted-foreground'>
                                {description}
                            </div>
                        )}
                    </div>

                    <button
                        type='button'
                        onClick={onClose}
                        className='-mt-1 -mr-2 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40'
                        aria-label='Tutup'
                    >
                        <X className='size-4' aria-hidden='true' />
                    </button>
                </div>

                {children && (
                    <div className='min-h-0 overflow-y-auto px-6 pb-5'>
                        {children}
                    </div>
                )}

                {footer && (
                    <div className='flex flex-col-reverse gap-2 border-t bg-muted/40 px-6 py-4 sm:flex-row sm:justify-end'>
                        {footer}
                    </div>
                )}
            </div>
        </dialog>
    )
}
